import React, { useState, useEffect } from 'react';
import { 
  Globe, 
  Key, 
  Lock,
  Code,
  Send,
  FileJson,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Check,
  Copy,
  PlayCircle,
  Edit,
  Save,
  Plus,
  Trash,
  MessageSquare,
  Server,
  Layers,
  Briefcase,
  Bug,
  TrendingUp,
  Play,
  Activity,
  RefreshCw,
  Loader,
  FileText,
  AlertCircle
} from 'lucide-react';

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  disabled = false,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Import dynamique de tous les fichiers JSON du répertoire api
const apiContext = require.context('../data/api', false, /\.json$/);

// Mapping des icônes par type de collection
const COLLECTION_ICONS = {
  'campaign': Briefcase,
  'core': Server,
  'environment': Layers,
  'security': Lock,
  'tasks': Activity,
  'agents': Server,
  'functions': Code,
  'messages': MessageSquare,
  'reports': FileText,
  'testcases': Bug,
  'executions': Play,
  'anomalies': AlertTriangle,
  'evolutions': TrendingUp
};

// Icône de catégorie basée sur le nom
const getCategoryIcon = (category) => {
  const iconMap = {
    campaigns: Briefcase,
    executions: Play,
    reports: FileText,
    testcases: Bug,
    agents: Server,
    messages: MessageSquare,
    environments: Layers,
    functions: Code,
    tasks: Activity,
    record: Activity,
    execute: Play,
    anomalies: Bug,
    evolutions: TrendingUp,
    utils: Server,
    authentication: Lock,
    users: Lock,
    roles: Lock
  };
  
  const categoryLower = category.toLowerCase();
  return iconMap[categoryLower] || Activity;
};

// Helper pour détecter le type de collection depuis les métadonnées ou le nom de fichier
const detectCollectionType = (data, filename) => {
  const name = data.info?.name?.toLowerCase() || '';
  const filenameLC = filename.toLowerCase();
  
  if (filenameLC.includes('campaign') || name.includes('campaign')) {
    return { icon: 'campaign', color: '#3B82F6' };
  }
  if (filenameLC.includes('core') || name.includes('core')) {
    return { icon: 'core', color: '#10B981' };
  }
  if (filenameLC.includes('environment') || name.includes('environment')) {
    return { icon: 'environment', color: '#6366F1' };
  }
  if (filenameLC.includes('security') || name.includes('security')) {
    return { icon: 'security', color: '#EC4899' };
  }
  
  return { icon: 'core', color: '#6B7280' };
};

// Validation de structure d'une collection Postman
const validateCollectionStructure = (data, filename) => {
  const errors = [];
  
  // Vérifier la structure basique Postman
  if (!data.info) {
    errors.push('Structure Postman invalide: info manquant');
  }
  
  if (!data.item || !Array.isArray(data.item)) {
    errors.push('Structure Postman invalide: item doit être un tableau');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings: []
  };
};

// Extraire les métadonnées d'une collection
const extractCollectionMetadata = (data, filename) => {
  const typeInfo = detectCollectionType(data, filename);
  const IconComponent = COLLECTION_ICONS[typeInfo.icon] || Server;
  
  return {
    filename,
    name: data.info?.name || filename.replace('.json', '').replace(/_/g, ' '),
    description: data.info?.description || `Collection API ${data.info?.name || ''}`,
    icon: IconComponent,
    color: typeInfo.color,
    version: data.info?.schema || '2.1.0',
    itemCount: data.item?.length || 0
  };
};

// Composant Badge pour les méthodes HTTP
const MethodBadge = ({ method }) => {
  const colors = {
    GET: 'bg-green-100 text-green-800',
    POST: 'bg-blue-100 text-blue-800',
    PUT: 'bg-yellow-100 text-yellow-800',
    DELETE: 'bg-red-100 text-red-800',
    PATCH: 'bg-purple-100 text-purple-800'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[method] || 'bg-gray-100 text-gray-800'}`}>
      {method}
    </span>
  );
};

// Composant de visualisation JSON
const JsonViewer = ({ data, title }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="relative">
      {title && (
        <div className="text-sm font-medium text-gray-700 mb-2">{title}</div>
      )}
      <div className="bg-gray-900 rounded-lg p-4 text-gray-100 font-mono text-sm overflow-x-auto">
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </div>
      <button
        onClick={copyToClipboard}
        className="absolute top-2 right-2 p-2 rounded-md hover:bg-gray-700 text-gray-400 hover:text-gray-200"
      >
        {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

// Parser pour transformer les données Postman en format unifié
const parsePostmanCollection = (collection) => {
  const endpoints = [];
  
  const parseItems = (items, parentPath = '') => {
    items.forEach(item => {
      if (item.item) {
        // C'est un dossier
        parseItems(item.item, item.name);
      } else if (item.request) {
        // C'est une requête
        const request = item.request;
        const endpoint = {
          id: item.name.toLowerCase().replace(/\s+/g, '-'),
          category: parentPath || 'General',
          name: item.name,
          method: request.method,
          path: request.url?.raw || '',
          description: request.description || item.name,
          headers: request.header || [],
          security: request.header?.find(h => h.key === 'Authorization') ? ['Bearer Token'] : [],
          requestBody: null,
          responses: []
        };

        // Parse URL
        if (request.url) {
          const urlParts = endpoint.path.replace('{{baseUrl}}', '').split('?');
          endpoint.path = urlParts[0];
          
          // Query parameters
          if (request.url.query) {
            endpoint.parameters = request.url.query.map(q => ({
              name: q.key,
              in: 'query',
              description: q.description || q.key,
              value: q.value
            }));
          }
        }

        // Parse request body
        if (request.body?.raw) {
          try {
            endpoint.requestBody = {
              type: 'application/json',
              example: JSON.parse(request.body.raw)
            };
          } catch (e) {
            endpoint.requestBody = {
              type: 'text/plain',
              example: request.body.raw
            };
          }
        }

        // Add test response if available
        if (item.response && item.response.length > 0) {
          endpoint.responses = item.response.map(resp => ({
            status: resp.code || 200,
            description: resp.name || 'Success',
            example: resp.body ? JSON.parse(resp.body) : null
          }));
        } else {
          // Default response
          endpoint.responses = [{
            status: 200,
            description: 'Success'
          }];
        }

        endpoints.push(endpoint);
      }
    });
  };

  if (collection.item) {
    parseItems(collection.item);
  }

  return {
    metadata: {
      title: collection.info.name,
      description: collection.info.description,
      version: collection.info.schema
    },
    endpoints
  };
};

// Composant de documentation d'endpoint
const EndpointDoc = ({ endpoint, serverConfig, authToken, onAuthTokenUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [testRequest, setTestRequest] = useState(
    endpoint.requestBody ? JSON.stringify(endpoint.requestBody.example, null, 2) : ''
  );
  const [testResponse, setTestResponse] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);

  const CategoryIcon = getCategoryIcon(endpoint.category);

  // Construire l'URL complète
  const buildFullUrl = () => {
    if (!serverConfig) {
      return endpoint.path;
    }
    const baseUrl = `${serverConfig.protocol}://${serverConfig.host}:${serverConfig.port}`;
    return `${baseUrl}${endpoint.path}`;
  };

  // Fonction pour tester l'API
  const handleTestRequest = async () => {
    if (!serverConfig) {
      setTestError('Configuration du serveur manquante');
      return;
    }

    setTestLoading(true);
    setTestError(null);
    setTestResponse(null);

    try {
      const url = buildFullUrl();
      const headers = {
        'Content-Type': 'application/json',
      };

      // Ajouter les headers de l'endpoint
      if (endpoint.headers) {
        endpoint.headers.forEach(header => {
          if (header.key && header.value) {
            // Remplacer les variables Postman
            let headerValue = header.value;
            if (header.value.includes('{{accessToken}}') && authToken) {
              headerValue = header.value.replace('{{accessToken}}', authToken);
            } else {
              headerValue = header.value.replace(/\{\{.*?\}\}/g, '');
            }
            headers[header.key] = headerValue;
          }
        });
      }

      // Ajouter le token Bearer si présent et si l'endpoint le requiert
      if (authToken && endpoint.security?.includes('Bearer Token')) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const options = {
        method: endpoint.method,
        headers,
      };

      if (endpoint.requestBody && testRequest) {
        options.body = testRequest;
      }

      console.log('Testing API:', { url, options });

      const response = await fetch(url, options);
      const data = await response.json();

      setTestResponse({
        status: response.status,
        statusText: response.statusText,
        data: data,
        headers: Object.fromEntries(response.headers.entries())
      });

      // Si c'est un endpoint de login et que la réponse contient un token
      if (endpoint.name?.toLowerCase().includes('login') && 
          response.status >= 200 && 
          response.status < 300) {
        
        // Chercher le token dans la réponse (accessToken, token, access_token, etc.)
        const possibleTokenFields = ['accessToken', 'token', 'access_token', 'authToken'];
        let foundToken = null;
        
        for (const field of possibleTokenFields) {
          if (data[field]) {
            foundToken = data[field];
            break;
          }
        }
        
        if (foundToken && onAuthTokenUpdate) {
          onAuthTokenUpdate(foundToken);
          setTestResponse(prev => ({
            ...prev,
            tokenSaved: true
          }));
        }
      }
    } catch (error) {
      setTestError(error.message);
      console.error('Test error:', error);
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex flex-row items-center justify-between">
          <div className="flex items-center space-x-4">
            <CategoryIcon className="h-5 w-5 text-blue-500" />
            <MethodBadge method={endpoint.method} />
            <div>
              <div className="font-mono text-sm">{endpoint.path}</div>
              <div className="text-sm text-gray-600 mt-1">{endpoint.name}</div>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            {isExpanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Description</h4>
              <p className="text-gray-600">{endpoint.description}</p>
            </div>

            {endpoint.parameters && endpoint.parameters.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Paramètres</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  {endpoint.parameters.map((param, index) => (
                    <div key={index} className="mb-2">
                      <span className="font-mono text-sm font-medium">{param.name}</span>
                      <span className="text-gray-600 ml-2">({param.in})</span>
                      <p className="text-sm text-gray-600 mt-1">{param.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {endpoint.requestBody && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Request Body</h4>
                <JsonViewer 
                  data={endpoint.requestBody.example} 
                  title="Example Request"
                />
              </div>
            )}

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Responses</h4>
              <div className="space-y-4">
                {endpoint.responses.map((response, index) => (
                  <div key={index}>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        response.status >= 200 && response.status < 300
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {response.status}
                      </span>
                      <span className="text-gray-600">{response.description}</span>
                    </div>
                    {response.example && (
                      <JsonViewer 
                        data={response.example} 
                        title="Example Response"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {endpoint.security && endpoint.security.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Sécurité</h4>
                <div className="flex space-x-2">
                  {endpoint.security.map((security, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                    >
                      {security}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {endpoint.headers && endpoint.headers.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Headers requis</h4>
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
                  {endpoint.headers.map((header, index) => (
                    <div key={index}>
                      {header.key}: {header.value}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Console de Test intégrée */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-4">Console de Test</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de test
                  </label>
                  <div className="p-3 bg-white border rounded-lg font-mono text-sm">
                    {buildFullUrl()}
                  </div>
                </div>

                {endpoint.requestBody && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Corps de la requête
                    </label>
                    <textarea
                      rows={6}
                      className="w-full p-3 border rounded-lg font-mono text-sm"
                      value={testRequest}
                      onChange={(e) => setTestRequest(e.target.value)}
                    />
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    className="flex items-center space-x-2"
                    onClick={handleTestRequest}
                    disabled={testLoading}
                  >
                    {testLoading ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Test en cours...</span>
                      </>
                    ) : (
                      <>
                        <PlayCircle className="h-4 w-4" />
                        <span>Tester</span>
                      </>
                    )}
                  </Button>
                </div>

                {/* Affichage de la réponse */}
                {testResponse && (
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-900 mb-2">Réponse</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                          testResponse.status >= 200 && testResponse.status < 300
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {testResponse.status} {testResponse.statusText}
                        </span>
                        {testResponse.tokenSaved && (
                          <span className="flex items-center text-green-600 text-sm">
                            <Check className="h-4 w-4 mr-1" />
                            Token sauvegardé automatiquement
                          </span>
                        )}
                      </div>
                      <JsonViewer 
                        data={testResponse.data} 
                        title="Response Body"
                      />
                    </div>
                  </div>
                )}

                {/* Affichage des erreurs */}
                {testError && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center space-x-2 text-red-600">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Erreur de test</span>
                    </div>
                    <p className="mt-1 text-sm text-red-600">{testError}</p>
                    <p className="mt-2 text-xs text-red-500">
                      Vérifiez que le serveur est accessible et que CORS est configuré correctement.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant principal de documentation API
const DocumentationAPI = () => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [serverConfig, setServerConfig] = useState({
    protocol: 'http',
    host: 'localhost',
    port: '8080'
  });
  const [authToken, setAuthToken] = useState(
    localStorage.getItem('api_auth_token') || ''
  );

  // Charger toutes les collections au montage
  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Démarrage du chargement des collections API...');
      
      const keys = apiContext.keys();
      const loadedCollections = [];
      const failedFiles = [];
      
      // Parcourir tous les fichiers trouvés
      keys.forEach(key => {
        const filename = key.replace('./', '');
        
        try {
          // Charger le fichier
          const data = apiContext(key);
          
          // Valider la structure
          const validation = validateCollectionStructure(data, filename);
          
          if (validation.isValid) {
            // Extraire les métadonnées
            const metadata = extractCollectionMetadata(data, filename);
            
            // Parser la collection
            const parsedData = parsePostmanCollection(data);
            
            loadedCollections.push({
              ...metadata,
              data: parsedData
            });
          } else {
            failedFiles.push({
              filename,
              errors: validation.errors
            });
          }
        } catch (err) {
          failedFiles.push({
            filename,
            error: err.message
          });
          console.error(`Erreur lors du chargement de ${filename}:`, err);
        }
      });
      
      console.log(`📁 ${loadedCollections.length} collections API chargées`);
      
      // Trier par nom
      loadedCollections.sort((a, b) => a.name.localeCompare(b.name));
      
      setCollections(loadedCollections);
      if (loadedCollections.length > 0) {
        setSelectedCollection(loadedCollections[0]);
        console.log(`✅ Première collection sélectionnée: ${loadedCollections[0].name}`);
      }
      
      // Log des fichiers avec erreurs (pour debug)
      if (failedFiles.length > 0) {
        console.log('⚠️ Fichiers avec erreurs:', failedFiles);
      }
    } catch (err) {
      setError('Erreur lors du chargement des collections API');
      console.error('Error loading collections:', err);
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder le token
  const saveAuthToken = (token) => {
    setAuthToken(token);
    localStorage.setItem('api_auth_token', token);
  };

  // Effacer le token
  const clearAuthToken = () => {
    setAuthToken('');
    localStorage.removeItem('api_auth_token');
  };

  // Obtenir toutes les catégories de la collection sélectionnée
  const getCategories = () => {
    if (!selectedCollection) return ['all'];
    const categories = new Set(selectedCollection.data.endpoints.map(e => e.category));
    return ['all', ...Array.from(categories)];
  };

  // Filtrer les endpoints
  const getFilteredEndpoints = () => {
    if (!selectedCollection) return [];
    
    let endpoints = selectedCollection.data.endpoints;
    
    // Filtre par catégorie
    if (selectedCategory !== 'all') {
      endpoints = endpoints.filter(e => e.category === selectedCategory);
    }
    
    // Filtre par recherche
    if (searchTerm) {
      endpoints = endpoints.filter(e => 
        e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return endpoints;
  };

  if (loading) {
    return (
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Chargement des collections API...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardContent>
            <div className="flex items-center space-x-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
            <Button 
              onClick={loadCollections} 
              className="mt-4"
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Réessayer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const categories = getCategories();
  const filteredEndpoints = getFilteredEndpoints();

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Configuration du serveur */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Server className="h-5 w-5 text-blue-500" />
            <span>Configuration du serveur</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Protocole
                </label>
                <select
                  value={serverConfig.protocol}
                  onChange={(e) => setServerConfig({...serverConfig, protocol: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="http">HTTP</option>
                  <option value="https">HTTPS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hôte / IP
                </label>
                <input
                  type="text"
                  value={serverConfig.host}
                  onChange={(e) => setServerConfig({...serverConfig, host: e.target.value})}
                  placeholder="localhost"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Port
                </label>
                <input
                  type="text"
                  value={serverConfig.port}
                  onChange={(e) => setServerConfig({...serverConfig, port: e.target.value})}
                  placeholder="8080"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end">
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL de base
                  </label>
                  <div className="px-3 py-2 bg-gray-100 rounded-md font-mono text-sm">
                    {`${serverConfig.protocol}://${serverConfig.host}:${serverConfig.port}`}
                  </div>
                </div>
              </div>
            </div>

            {/* Section Token d'authentification */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Token d'authentification
                </label>
                {authToken && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAuthToken}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash className="h-4 w-4 mr-1" />
                    Effacer
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={authToken}
                  onChange={(e) => saveAuthToken(e.target.value)}
                  placeholder="Bearer token..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
                <div className={`px-3 py-2 rounded-md text-sm ${
                  authToken ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {authToken ? (
                    <div className="flex items-center">
                      <Lock className="h-4 w-4 mr-1" />
                      Token actif
                    </div>
                  ) : (
                    'Pas de token'
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Le token sera automatiquement sauvegardé après un login réussi
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* En-tête */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Documentation API
            </h1>
            <p className="mt-1 text-gray-600">
              {collections.length} collections chargées
            </p>
          </div>
          <Button
            variant="outline"
            onClick={loadCollections}
            className="flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Actualiser</span>
          </Button>
        </div>
      </div>

      {/* Sélection de collection */}
      <div className="mb-6">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {collections.map((collection) => {
            const Icon = collection.icon;
            return (
              <button
                key={collection.filename}
                onClick={() => {
                  setSelectedCollection(collection);
                  setSelectedCategory('all');
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center space-x-2 ${
                  selectedCollection?.filename === collection.filename
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{collection.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {selectedCollection && (
        <>
          {/* Informations de la collection */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileJson className="h-5 w-5 text-blue-500" />
                <span>{selectedCollection.data.metadata.title}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                {selectedCollection.data.metadata.description}
              </p>
              <div className="mt-4 text-sm text-gray-500">
                {filteredEndpoints.length} endpoints
              </div>
            </CardContent>
          </Card>

          {/* Barre de recherche */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un endpoint..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Globe className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>

          {/* Filtre par catégorie */}
          <div className="mb-6">
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {categories.map((category) => {
                const CategoryIcon = getCategoryIcon(category);
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex items-center space-x-2 ${
                      selectedCategory === category
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category !== 'all' && <CategoryIcon className="h-4 w-4" />}
                    <span>{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Liste des endpoints */}
          <div className="space-y-6">
            {filteredEndpoints.length > 0 ? (
              filteredEndpoints.map((endpoint, index) => (
                <EndpointDoc
                  key={`${endpoint.category}-${endpoint.id}-${index}`}
                  endpoint={endpoint}
                  serverConfig={serverConfig}
                  authToken={authToken}
                  onAuthTokenUpdate={saveAuthToken}
                />
              ))
            ) : (
              <Card>
                <CardContent>
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      Aucun endpoint trouvé
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentationAPI;